/**
 * Canvas JSON in millimetre space
 *
 * The stored canvas JSON of a design position is the area-relative Fabric
 * JSON divided by the Pixel Scale of the template it was written on (R34).
 * The conversion is an origin-anchored similarity: only `left`, `top`,
 * `scaleX` and `scaleY` change. Every intrinsic property (an image's source
 * crop, a text's `fontSize` and `charSpacing`, a path's points, a stroke's
 * width) stays, because Fabric scales them through the object matrix.
 *
 * Properties Fabric interprets in pixels outside that matrix are pixel
 * context the JSON must not carry (`stripPixelContext`); the editor sets
 * the area clip from the template rectangle on load instead.
 *
 * No placement rule lives here.
 */

import {
  SYSTEM_LAYER_NAME_SET,
  type CanvasObjectJSON,
} from '../design/computeDesignState.js';

/** Top-level properties Fabric reads as pixels outside the object matrix. */
export const PIXEL_CONTEXT_KEYS = [
  'clipPath',
  'strokeUniform',
  'absolutePositioned',
] as const;

/** A serialized canvas: the objects plus whatever else Fabric wrote. */
export interface CanvasJsonLike<T extends CanvasObjectJSON = CanvasObjectJSON> {
  objects?: T[];
  [key: string]: unknown;
}

/**
 * Scales the position and scale of every user object by one factor. System
 * layers pass through untouched; nested group children keep their
 * group-relative coordinates, which the group's own scale carries.
 */
export function scaleCanvasObjects<T extends CanvasObjectJSON>(
  objects: readonly T[],
  factor: number
): T[] {
  return objects.map((obj) => {
    if (SYSTEM_LAYER_NAME_SET.has(obj.name ?? '')) return obj;
    const out: T = { ...obj };
    if (typeof obj.left === 'number') out.left = obj.left * factor;
    if (typeof obj.top === 'number') out.top = obj.top * factor;
    out.scaleX = (obj.scaleX ?? 1) * factor;
    out.scaleY = (obj.scaleY ?? 1) * factor;
    return out;
  });
}

/** Area-relative canvas pixels of a template → millimetre space (AE19). */
export function canvasJsonToMm<T extends CanvasObjectJSON>(
  json: CanvasJsonLike<T>,
  pxPerMm: number
): CanvasJsonLike<T> {
  return {
    ...json,
    objects: scaleCanvasObjects(json.objects ?? [], 1 / pxPerMm),
  };
}

/** Millimetre space → area-relative canvas pixels of a template (AE19). */
export function canvasJsonFromMm<T extends CanvasObjectJSON>(
  json: CanvasJsonLike<T>,
  pxPerMm: number
): CanvasJsonLike<T> {
  return { ...json, objects: scaleCanvasObjects(json.objects ?? [], pxPerMm) };
}

/**
 * Removes the pixel context a serializer must not persist: the per-object
 * area clip, uniform strokes, absolute clip positioning and a non-scaling
 * shadow. Returns a new object; nested groups are cleaned as well.
 */
export function stripPixelContext<T extends CanvasObjectJSON>(obj: T): T {
  const record = { ...obj } as Record<string, unknown>;
  for (const key of PIXEL_CONTEXT_KEYS) delete record[key];
  const shadow = record.shadow;
  if (typeof shadow === 'object' && shadow !== null) {
    const rest: Record<string, unknown> = { ...shadow };
    delete rest.nonScaling;
    record.shadow = rest;
  }
  if (Array.isArray(obj.objects)) {
    record.objects = obj.objects.map(stripPixelContext);
  }
  return record as T;
}
